import { DateUtil } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import moment from 'moment';
import Constants from '../../constants';
import ContentConstants from '../../contentConstants';
import {
	AudiencePropertiesUnderValidation,
	DistributionListUnderValidation,
	DistributionType,
	EmailTemplatePropertiesUnderValidation,
	IAction,
	IAudience,
	IDistributionFile,
	IEmailTemplate,
	IValidationErrorCollection,
} from '../../models';
import { TextHelper } from '../../utilities/TextHelper';

export function validateAudienceCloseDates(audienceOpenOn: any, audienceCloseOn: any) {
	const errors = errorsForCloseDates(audienceOpenOn, audienceCloseOn);
	return errors.length > 0 ? { [AudiencePropertiesUnderValidation.OpenCloseDates]: errors } : undefined;
}

export function validateAudienceOpenCloseDates(audienceOpenOn: any, audienceCloseOn: any, openImmediately: boolean, isOpen?: boolean) {
	const errors = [];
	const openOn = (audienceOpenOn && DateUtil.getLocalMoment(new Date(audienceOpenOn))) || undefined;

	if (!isOpen && !openImmediately && openOn && openOn.valueOf() < moment().valueOf()) {
		errors.push(ContentConstants.AUDIENCE_ERROR_INVALID_OPENDATE);
	}
	const closeErrors = errorsForCloseDates(audienceOpenOn, audienceCloseOn);
	const combinedErrors = errors.concat(closeErrors);
	return combinedErrors.length > 0 ? { [AudiencePropertiesUnderValidation.OpenCloseDates]: combinedErrors } : undefined;
}

export function errorsForCloseDates(audienceOpenOn: any, audienceCloseOn: any) {
	const errors = [];
	const openOn = (audienceOpenOn && DateUtil.getLocalMoment(new Date(audienceOpenOn))) || undefined;
	const closeOn = (audienceCloseOn && DateUtil.getLocalMoment(new Date(audienceCloseOn))) || undefined;

	if (closeOn && closeOn.valueOf() < moment().valueOf()) {
		errors.push(ContentConstants.AUDIENCE_ERROR_INVALID_CLOSEDATE);
	}
	if (openOn && closeOn && openOn.valueOf() >= closeOn.valueOf()) {
		errors.push(ContentConstants.AUDIENCE_ERROR_OPENDATE_GREATER_THAN_CLOSEDATE);
	}
	return errors;
}

export function validAudienceFields(fieldNames: AudiencePropertiesUnderValidation[], audience: IAudience, isOpen?: boolean) {
	const errors: IValidationErrorCollection = {};
	fieldNames.forEach((fieldName: AudiencePropertiesUnderValidation) => {
		switch (fieldName) {
			case AudiencePropertiesUnderValidation.AudienceName: {
				const isValid = audience.audienceName && audience.audienceName.length > 0;
				if (!isValid) {
					errors[AudiencePropertiesUnderValidation.AudienceName] = [ContentConstants.ENTER_VALUE_REQUIRED];
				}
				break;
			}
			case AudiencePropertiesUnderValidation.OpenOn: {
				if (!audience.openOn && !audience.openImmediately) {
					errors[AudiencePropertiesUnderValidation.OpenOn] = [ContentConstants.SELECT_VALUE_REQUIRED];
				}
				break;
			}
			case AudiencePropertiesUnderValidation.CloseOn: {
				if (!audience.closeOn) {
					errors[AudiencePropertiesUnderValidation.CloseOn] = [ContentConstants.SELECT_VALUE_REQUIRED];
				}
				break;
			}
			case AudiencePropertiesUnderValidation.OpenCloseDates: {
				const isValid = audience.openImmediately ? audience.closeOn : audience.openOn && audience.closeOn;
				if (isValid) {
					const openCloseDateErrors = validateAudienceOpenCloseDates(audience.openOn, audience.closeOn, audience.openImmediately, isOpen);
					if (openCloseDateErrors) {
						errors[AudiencePropertiesUnderValidation.OpenCloseDates] =
							openCloseDateErrors[AudiencePropertiesUnderValidation.OpenCloseDates];
					}
				}
				break;
			}
			case AudiencePropertiesUnderValidation.DistributionType: {
				const isValid = audience.distributionType;
				if (!isValid) {
					errors[AudiencePropertiesUnderValidation.DistributionType] = [ContentConstants.AUDIENCE_ERROR_DISTRIBUTION_TYPE];
				}
				break;
			}
		}
	});

	return Object.keys(errors).length > 0 ? errors : undefined;
}

export function validEmailTemplateFields(
	fieldNames: EmailTemplatePropertiesUnderValidation[],
	emailTemplate: IEmailTemplate,
	distributionType?: DistributionType,
) {
	const errors: IValidationErrorCollection = {};
	fieldNames.forEach((fieldName: EmailTemplatePropertiesUnderValidation) => {
		switch (fieldName) {
			case EmailTemplatePropertiesUnderValidation.Name: {
				const isValid = emailTemplate.emailTemplateName && emailTemplate.emailTemplateName.length > 0;
				if (!isValid) {
					errors[EmailTemplatePropertiesUnderValidation.Name] = [ContentConstants.ENTER_VALUE_REQUIRED];
				}
				break;
			}
			case EmailTemplatePropertiesUnderValidation.From: {
				const isValid = emailTemplate.from && emailTemplate.from.length > 0;
				if (!isValid) {
					errors[EmailTemplatePropertiesUnderValidation.From] = [ContentConstants.ENTER_VALUE_REQUIRED];
				}
				break;
			}
			case EmailTemplatePropertiesUnderValidation.ReplyToEmail: {
				const isValid = emailTemplate.replyToEmail && emailTemplate.replyToEmail.length > 0;
				if (!isValid) {
					errors[EmailTemplatePropertiesUnderValidation.ReplyToEmail] = [ContentConstants.ENTER_VALUE_REQUIRED];
				}
				if (emailTemplate.replyToEmail) {
					if (!TextHelper.isValidEmail(emailTemplate.replyToEmail)) {
						errors[EmailTemplatePropertiesUnderValidation.ReplyToEmail] = [ContentConstants.PLACEHOLDER_REPORTING_ERROR_ENTER_EMAIL];
					}
				}
				break;
			}
			case EmailTemplatePropertiesUnderValidation.ScheduledFor: {
				const isValid = emailTemplate.scheduledFor;
				if (!isValid) {
					errors[EmailTemplatePropertiesUnderValidation.ScheduledFor] = [ContentConstants.SELECT_VALUE_REQUIRED];
				}
				break;
			}
			case EmailTemplatePropertiesUnderValidation.Subject: {
				const isValid = emailTemplate.subject && emailTemplate.subject.length > 0;
				if (!isValid) {
					errors[EmailTemplatePropertiesUnderValidation.Subject] = [ContentConstants.ENTER_VALUE_REQUIRED];
				}
				break;
			}
			case EmailTemplatePropertiesUnderValidation.Content: {
				const hasContent = emailTemplate.content && emailTemplate.content.length > 0;
				if (!hasContent) {
					errors[EmailTemplatePropertiesUnderValidation.Content] = [ContentConstants.ENTER_VALUE_REQUIRED];
				} else {
					/*  
						Server-side validation treats each character as a double-byte character and considers
						1 kilobyte to equal 1000 bytes instead of 1024. To match that math, the content length needs
				   		to be doubled to treat each character as a double-byte character and then divided
						by 1000 to convert from byte to kilobyte.  
					*/
					const emailContentSizeInKB = (emailTemplate.content.length * 2) / 1000;
					const isContentAboveLimit = emailContentSizeInKB > Constants.EMAIL_CONTENT_LIMIT_IN_KB;
					if (isContentAboveLimit) {
						errors[EmailTemplatePropertiesUnderValidation.Content] = [ContentConstants.AUDIENCE_ERROR_EMAIL_CONTENT_TOO_LARGE];
					}
				}

				break;
			}
			case EmailTemplatePropertiesUnderValidation.SurveyLink: {
				if (distributionType !== DistributionType.SSO) {
					const isEmailContentValid = emailTemplate.content && emailTemplate.content.length > 0;
					if (isEmailContentValid) {
						const count: number = (emailTemplate.content.match(Constants.SURVEY_LINK_REGEX) || []).length;
						const isValid = count > 0;
						if (!isValid) {
							errors[EmailTemplatePropertiesUnderValidation.SurveyLink] = [ContentConstants.AUDIENCE_ERROR_SURVEY_LINK_NOT_EXIST];
						}
					}
				}
				break;
			}
		}
	});

	return Object.keys(errors).length > 0 ? errors : undefined;
}

export function validateReminderNameForDuplicity(reminderEmails: IEmailTemplate[], reminderEmail: IEmailTemplate) {
	const errors: IValidationErrorCollection = {};
	if (reminderEmail.emailTemplateName) {
		const duplicateReminders = reminderEmails.filter(
			(re: IEmailTemplate) =>
				re.emailTemplateName &&
				re.emailTemplateName.toLowerCase() === reminderEmail.emailTemplateName!.toLowerCase() &&
				re.uniqueId !== reminderEmail.uniqueId,
		);

		if (duplicateReminders && duplicateReminders.length > 0) {
			errors[EmailTemplatePropertiesUnderValidation.Name] = [ContentConstants.AUDIENCE_ERROR_REMINDER_EMAIL_DUPLICATE_NAME];
		}
	}

	return Object.keys(errors).length > 0 ? errors : undefined;
}

export function validEmailFields(emails: string) {
	const errors: string[] = [];
	const isValid = emails && emails.length > 0;
	if (!isValid) {
		errors.push(ContentConstants.AUDIENCE_ERROR_NO_EMAIL_ADDRESSES);
	} else {
		const emailAddresses: string[] = emails.split(',');
		let invalidEmailAddresses = false;
		for (const emailAddress of emailAddresses) {
			if (!TextHelper.isValidEmail(emailAddress)) {
				invalidEmailAddresses = true;
				break;
			}
		}
		if (invalidEmailAddresses) {
			errors.push(ContentConstants.AUDIENCE_ERROR_INVALID_EMAIL_ADDRESSES);
		}
		if (emailAddresses.length > Constants.EMAIL_ADDRESSES_LIMIT) {
			const error = _.replace(
				ContentConstants.AUDIENCE_ERROR_EXCEED_EMAIL_ADDRESS_LIMIT,
				new RegExp('{limit}', 'g'),
				Constants.EMAIL_ADDRESSES_LIMIT.toString(),
			);
			errors.push(error);
		}
	}

	return errors.length > 0
		? {
				EmailAddresses: errors,
		  }
		: undefined;
}

export function validDistributionList(distribution?: IDistributionFile, file?: any) {
	const errors: IValidationErrorCollection = {};
	const isValid = (distribution && distribution.action !== IAction.Delete) || file;
	if (!isValid) {
		errors[DistributionListUnderValidation.DistributionList] = [ContentConstants.DISTRIBUTION_VALUE_REQUIRED];
	}

	return Object.keys(errors).length > 0 ? errors : undefined;
}
